import type { FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, CircleCheck, LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { inquiryTypes } from '@/lib/contact'
import { buttonClasses } from '@/components/kit/button'

type Status = 'idle' | 'sending' | 'sent' | 'error'

const fieldClass = 'w-full rounded-lg border border-input bg-obsidian/60 px-4 text-base text-foreground placeholder:text-muted-foreground/70 transition focus:border-gold/60 focus:outline-none aria-[invalid=true]:border-[#D06A7C]'

export const ContactForm: FC<{ initialType?: string }> = ({ initialType }): ReactNode => {
    const [type, setType] = useState<string>(inquiryTypes.some(item => item.value === initialType) ? initialType as string : 'professional')
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState<string | null>(null)
    const [fields, setFields] = useState<Record<string, string>>({})
    const startedAt = useRef(0)

    useEffect(() => {
        startedAt.current = Date.now()
    }, [])

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const form = new FormData(event.currentTarget)

        setStatus('sending')
        setError(null)
        setFields({})

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type,
                    name: form.get('name'),
                    email: form.get('email'),
                    organisation: form.get('organisation'),
                    subject: form.get('subject'),
                    message: form.get('message'),
                    website: form.get('website'),
                    startedAt: startedAt.current
                })
            })

            const payload = await response.json().catch(() => null)

            if (!response.ok || !payload?.ok) {
                setFields(payload?.fields ?? {})
                setError(payload?.error ?? 'Your message could not be sent. Please try again.')
                setStatus('error')

                return
            }

            setStatus('sent')
        }
        catch {
            setError('Your message could not be sent. Please check your connection and try again.')
            setStatus('error')
        }
    }

    if (status === 'sent') {
        return (
            <div className='rounded-2xl border border-gold/30 bg-gold/[0.06] p-10 text-center animate-in fade-in duration-500' role='status'>
                <CircleCheck className='mx-auto size-10 text-gold' aria-hidden='true' />
                <p className='mt-6 font-heading text-4xl font-light'>Your message has been received.</p>
                <p className='mx-auto mt-4 max-w-md text-base leading-relaxed text-muted-foreground'>
                    Thank you for writing. Every message is read personally, and a reply will come to the email address you gave.
                </p>
                <button type='button' onClick={() => setStatus('idle')} className='mt-8 text-sm text-gold underline-offset-4 hover:underline'>
                    Send another message
                </button>
            </div>
        )
    }

    return (
        <form onSubmit={submit} noValidate className='space-y-8' aria-describedby={error ? 'contact-error' : undefined}>
            <fieldset>
                <legend className='eyebrow mb-4 text-gold'>What is it about?</legend>

                <div className='grid gap-2 sm:grid-cols-2'>
                    {inquiryTypes.map(item => (
                        <label
                            key={item.value}
                            className={cn(
                                'flex cursor-pointer flex-col gap-1 rounded-lg border p-4 transition has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-gold',
                                type === item.value ? 'border-gold/60 bg-ivory/[0.05]' : 'border-white/[0.08] hover:border-white/20'
                            )}
                        >
                            <input type='radio' name='type' value={item.value} checked={type === item.value} onChange={() => setType(item.value)} className='sr-only' />
                            <span className='font-heading text-xl'>{item.label}</span>
                            <span className='text-xs text-muted-foreground'>{item.hint}</span>
                        </label>
                    ))}
                </div>
            </fieldset>

            <div className='grid gap-6 sm:grid-cols-2'>
                <div>
                    <label htmlFor='contact-name' className='mb-2 block text-sm text-ivory/80'>Your name</label>
                    <input id='contact-name' name='name' type='text' autoComplete='name' required maxLength={120} aria-invalid={Boolean(fields.name)} aria-describedby={fields.name ? 'contact-name-error' : undefined} className={cn(fieldClass, 'h-12')} />
                    {fields.name && <p id='contact-name-error' className='mt-2 text-sm text-[#D06A7C]'>{fields.name}</p>}
                </div>

                <div>
                    <label htmlFor='contact-email' className='mb-2 block text-sm text-ivory/80'>Email address</label>
                    <input id='contact-email' name='email' type='email' autoComplete='email' required maxLength={254} aria-invalid={Boolean(fields.email)} aria-describedby={fields.email ? 'contact-email-error' : undefined} className={cn(fieldClass, 'h-12')} />
                    {fields.email && <p id='contact-email-error' className='mt-2 text-sm text-[#D06A7C]'>{fields.email}</p>}
                </div>

                <div>
                    <label htmlFor='contact-organisation' className='mb-2 block text-sm text-ivory/80'>
                        Organisation <span className='text-muted-foreground'>(optional)</span>
                    </label>
                    <input id='contact-organisation' name='organisation' type='text' autoComplete='organization' maxLength={160} className={cn(fieldClass, 'h-12')} />
                </div>

                <div>
                    <label htmlFor='contact-subject' className='mb-2 block text-sm text-ivory/80'>
                        Subject <span className='text-muted-foreground'>(optional)</span>
                    </label>
                    <input id='contact-subject' name='subject' type='text' maxLength={200} className={cn(fieldClass, 'h-12')} />
                </div>
            </div>

            <div>
                <label htmlFor='contact-message' className='mb-2 block text-sm text-ivory/80'>Message</label>
                <textarea id='contact-message' name='message' required rows={7} maxLength={5000} aria-invalid={Boolean(fields.message)} aria-describedby={fields.message ? 'contact-message-error' : undefined} className={cn(fieldClass, 'resize-y py-3 leading-relaxed')} />
                {fields.message && <p id='contact-message-error' className='mt-2 text-sm text-[#D06A7C]'>{fields.message}</p>}
            </div>

            <div className='hidden' aria-hidden='true'>
                <label htmlFor='contact-website'>Leave this field empty</label>
                <input id='contact-website' name='website' type='text' tabIndex={-1} autoComplete='off' />
            </div>

            {error && (
                <p id='contact-error' role='alert' className='rounded-lg border border-burgundy/50 bg-burgundy/10 px-4 py-3 text-sm text-ivory'>
                    {error}
                </p>
            )}

            <div className='flex flex-wrap items-center justify-between gap-4'>
                <p className='max-w-sm text-xs leading-relaxed text-muted-foreground'>
                    Your details are used only to reply to you. See the <Link href='/privacy' className='underline underline-offset-2 hover:text-ivory'>privacy note</Link>.
                </p>

                <button type='submit' disabled={status === 'sending'} className={buttonClasses('primary', 'lg')}>
                    {status === 'sending' ? <LoaderCircle className='animate-spin' aria-hidden='true' /> : null}
                    {status === 'sending' ? 'Sending' : 'Send message'}
                    {status !== 'sending' && <ArrowRight aria-hidden='true' />}
                </button>
            </div>
        </form>
    )
}
